import React from 'react';
import {
    Landmark, ArrowDownLeft, ArrowUpRight, Banknote, Smartphone, CreditCard, Receipt, Clock, Users, Scissors,
    TrendingUp, Lock, LockOpen, CheckCircle2, AlertTriangle, Crown, Trophy, Wallet, Truck,
} from 'lucide-react';
import styles from './OwnerClosing.module.css';
import { inr, longDate } from '../../utils/money';

const Row = ({ label, value, sign, hint, strong }) => {
    if (!strong && !value && sign) return null;   // hide empty optional lines
    return (
        <div className={`${styles.row} ${strong ? styles.rowStrong : ''}`}>
            <span>{label}{hint && <small>{hint}</small>}</span>
            <b className={sign === '+' && value ? styles.pos : sign === '-' && value ? styles.neg : ''}>
                {sign && value ? (sign === '+' ? '+ ' : '− ') : ''}{inr(value)}
            </b>
        </div>
    );
};

const Bar = ({ icon: Icon, label, value, max, tone }) => (
    <div className={styles.bar}>
        <span className={`${styles.barIcon} ${styles[tone]}`}><Icon size={13} /></span>
        <div>
            <div className={styles.barTop}><span>{label}</span><b>{inr(value)}</b></div>
            <i className={styles.track}><em className={styles[`${tone}Fill`]} style={{ width: `${max > 0 ? Math.max(value > 0 ? 3 : 0, (value / max) * 100) : 0}%` }} /></i>
        </div>
    </div>
);

/** Owner's day-closing summary (view only), shown on the shared link */
export default function OwnerClosing({ report }) {
    const { totals: t, payments: p, drawer: d, dayResult: r, expenses: ex = {}, outside = {}, store } = report;
    const closing = d.closing;
    const diff = closing?.difference;
    const inMax = Math.max(p.cash, p.upi, p.card, outside.total || 0, 1);
    const catMax = Math.max(...(ex.byCategory || []).map((c) => c.amount), 1);
    const vp = report.vendorPurchases || { count: 0, items: [] };   // stock bought from vendors today
    const expItems = ex.items || [];
    const modeName = (m, label) => ({ CASH: 'Cash', UPI: 'UPI', BANK: 'Bank', CARD: 'Card', CHEQUE: 'Cheque' }[m] || label || 'Not set');

    return (
        <div className={styles.page}>
            {/* ---------- hero ---------- */}
            <header className={styles.hero}>
                <div className={styles.heroTop}>
                    <div className={styles.store}>
                        {store?.logo ? <img src={store.logo} alt="" /> : <span>{(store?.name || 'S').charAt(0)}</span>}
                        <div><strong>{store?.name || 'Branch'}</strong><small>{longDate(report.date)}</small></div>
                    </div>
                    <span className={`${styles.chip} ${closing ? styles.chipClosed : styles.chipOpen}`}>
                        {closing ? <><Lock size={11} /> Closed</> : <><LockOpen size={11} /> Not closed</>}
                    </span>
                </div>
                <span className={styles.heroLabel}>{r.net < 0 ? 'Spent more than earned today' : 'Left today'}</span>
                <strong className={`${styles.heroValue} ${r.net < 0 ? styles.heroLoss : ''}`}>{inr(r.net)}</strong>
                <div className={styles.flow}>
                    <div><ArrowDownLeft size={13} /><span>Money in</span><b>{inr(r.moneyIn)}</b></div>
                    <i>−</i>
                    <div><ArrowUpRight size={13} /><span>Expenses</span><b>{inr(r.expensesTotal)}</b></div>
                </div>
                {r.boughtOnCredit > 0 && (
                    <p className={styles.creditNote}><Truck size={12} /> Bought on credit today {inr(r.boughtOnCredit)}: to pay vendors later, not counted above</p>
                )}
            </header>

            {/* ---------- cash drawer ---------- */}
            <section className={styles.card}>
                <h2><Landmark size={15} /> Cash drawer</h2>
                <Row label="Opening cash" value={d.opening} hint={d.openingSource} />
                <Row label="Cash from bills" value={d.cashSales} sign="+" />
                <Row label="Old udhaar / advance in cash" value={d.outsideCash} sign="+" />
                <Row label="Cash given by owner / other" value={d.ownerCashIn} sign="+" />
                <Row label="Deposited in bank" value={d.bankDeposit} sign="-" />
                <Row label="Petty cash" value={d.pettyExpense} sign="-" />
                <Row label="Owner took" value={d.ownerWithdrawal} sign="-" />
                <Row label="Other cash out" value={d.otherOut} sign="-" />
                <Row label={ex.vendor?.paidCash ? 'Expenses + vendors paid in cash' : 'Expenses paid in cash'} value={d.expensesCash} sign="-" hint={ex.vendor?.paidCash ? `Vendors ${inr(ex.vendor.paidCash)}` : ''} />
                <Row label="Should be in the drawer" value={d.expected} strong />
                {d.expected < -0.009 && <p className={styles.belowZero}><AlertTriangle size={13} /> Below zero: {inr(Math.abs(d.expected))} was paid from someone's pocket.</p>}
                {closing ? (
                    <div className={`${styles.count} ${Math.abs(diff) < 0.01 ? styles.countOk : diff < 0 ? styles.countShort : styles.countExtra}`}>
                        {Math.abs(diff) < 0.01 ? <CheckCircle2 size={18} /> : <AlertTriangle size={18} />}
                        <div>
                            <strong>Counted {inr(closing.countedCash)}</strong>
                            <span>{Math.abs(diff) < 0.01 ? 'Matches exactly' : `${diff < 0 ? 'Short by' : 'Extra'} ${inr(Math.abs(diff))}`}{closing.note ? ` · ${closing.note}` : ''}</span>
                        </div>
                    </div>
                ) : (
                    <div className={`${styles.count} ${styles.countPending}`}>
                        <Clock size={18} />
                        <div><strong>Drawer not counted yet</strong><span>The count shows here once the day is closed.</span></div>
                    </div>
                )}
            </section>

            {/* ---------- money in ---------- */}
            <section className={styles.card}>
                <h2><ArrowDownLeft size={15} /> Money in <em>{inr(r.moneyIn)}</em></h2>
                <Bar icon={Banknote} label="Cash" value={p.cash} max={inMax} tone="green" />
                <Bar icon={Smartphone} label="UPI" value={p.upi} max={inMax} tone="purple" />
                <Bar icon={CreditCard} label="Card" value={p.card} max={inMax} tone="dark" />
                {outside.total > 0 && <Bar icon={Wallet} label="Old udhaar / advance received" value={outside.total} max={inMax} tone="amber" />}
                <div className={styles.notes}>
                    {p.advanceUsed > 0 && <span>Khata / prepaid used <b>{inr(p.advanceUsed)}</b></span>}
                    {p.dueCreated > 0 && <span className={styles.noteRed}>Udhaar given <b>{inr(p.dueCreated)}</b></span>}
                </div>
            </section>

            {/* ---------- expenses ---------- */}
            <section className={styles.card}>
                <h2><Receipt size={15} /> Expenses <em className={styles.neg}>{inr(r.expensesTotal)}</em></h2>
                {(ex.byCategory || []).length === 0 && !d.pettyExpense ? <p className={styles.empty}>No expenses paid today.</p> : (
                    <>
                        {(ex.byCategory || []).map((c) => <Bar key={c.name} icon={Receipt} label={c.name} value={c.amount} max={catMax} tone="pink" />)}
                        <div className={styles.split}>
                            <div><span>Paid in cash</span><b>{inr(r.expensesCash)}</b></div>
                            <div><span>Paid online</span><b>{inr(r.expensesOnline)}</b></div>
                        </div>
                        {ex.vendor?.paid > 0 && <p className={styles.subNote}><Truck size={12} /> {inr(ex.vendor.paid)} of this was paid to vendors{ex.vendor.notCleared > 0 ? `, ${inr(ex.vendor.notCleared)} not cleared yet` : ''}.</p>}
                        {ex.pendingCount > 0 && <p className={styles.pending}><Clock size={12} /> {ex.pendingCount} waiting for approval ({inr(ex.pending)})</p>}
                        {expItems.length > 0 && (
                            <ul className={styles.items}>
                                {expItems.slice(0, 40).map((x, i) => (
                                    <li key={i}>
                                        <span className={styles.itemText}>
                                            <b>{x.title}</b>
                                            <small>
                                                {x.vendor ? <em className={styles.tagVendor}>Vendor</em> : x.category}{x.subcategory && !x.vendor ? ` · ${x.subcategory}` : ''}
                                                {' · '}{modeName(x.mode, x.modeLabel)}
                                                {x.clearPending && <em className={styles.tagWarn}>Not cleared</em>}
                                                {x.status === 'pending' && <em className={styles.tagWarn}>Waiting approval</em>}
                                            </small>
                                        </span>
                                        <b className={x.mode === 'CASH' ? styles.neg : ''}>{inr(x.amount)}</b>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </>
                )}
            </section>

            {/* ---------- stock bought from vendors today ---------- */}
            {vp.count > 0 && (
                <section className={styles.card}>
                    <h2><Truck size={15} /> Vendor purchases today <em>{inr(vp.total)}</em></h2>
                    <div className={styles.split}>
                        <div><span>Paid</span><b>{inr(vp.paid)}</b></div>
                        <div><span>To pay later</span><b className={vp.toPay > 0 ? styles.neg : ''}>{inr(vp.toPay)}</b></div>
                    </div>
                    <ul className={styles.items}>
                        {vp.items.map((b) => (
                            <li key={b.id}>
                                <span className={styles.itemText}>
                                    <b>{b.vendorName}{b.billNo ? <small style={{ display: 'inline', marginLeft: 6 }}>bill {b.billNo}</small> : null}</b>
                                    <small>{b.products.slice(0, 3).map((x) => `${x.qty} × ${x.name}`).join(', ')}{b.products.length > 3 ? ` +${b.products.length - 3} more` : ''}{b.gst > 0 ? ` · GST ${inr(b.gst)}` : ''}</small>
                                    <small>
                                        {b.paidVia.length ? b.paidVia.map((x, i) => <span key={i}>{i ? ', ' : ''}{modeName(x.mode, x.modeLabel)} {inr(x.amount)}{!x.cleared && <em className={styles.tagWarn}>Not cleared</em>}</span>) : 'Not paid yet'}
                                    </small>
                                </span>
                                <span className={styles.billSide}>
                                    <b>{inr(b.total)}</b>
                                    <em className={b.status === 'PAID' ? styles.tagPaid : styles.tagDue}>{b.status === 'PAID' ? 'Paid' : `${inr(b.toPay)} to pay`}</em>
                                </span>
                            </li>
                        ))}
                    </ul>
                </section>
            )}

            {/* ---------- business ---------- */}
            <section className={styles.card}>
                <h2><TrendingUp size={15} /> Business</h2>
                <div className={styles.stats}>
                    <div><span>Total sales</span><b>{inr(t.grossSales)}</b></div>
                    <div><span>Bills</span><b>{t.bills}</b></div>
                    <div><span><Users size={11} /> Clients</span><b>{t.customersServed}{t.newClients ? <small> ({t.newClients} new)</small> : null}</b></div>
                    <div><span>Average bill</span><b>{inr(t.avgBill)}</b></div>
                    <div><span><Scissors size={11} /> Services</span><b>{t.serviceCount}</b></div>
                    <div><span><Crown size={11} /> Memberships sold</span><b>{inr(report.sales?.loyaltySold)}</b></div>
                    <div><span>Discounts</span><b>{inr(report.sales?.discounts)}</b></div>
                    <div><span>GST</span><b>{inr(report.sales?.gst)}</b></div>
                </div>
            </section>

            {(report.staff?.length > 0 || report.topServices?.length > 0) && (
                <section className={styles.card}>
                    {report.staff?.length > 0 && (
                        <>
                            <h2><Trophy size={15} /> Staff today</h2>
                            <ol className={styles.list}>
                                {report.staff.slice(0, 5).map((s, i) => (
                                    <li key={s.name}><i>{i + 1}</i><span>{s.name}<small>{s.services} services</small></span><b>{inr(s.credit)}</b></li>
                                ))}
                            </ol>
                        </>
                    )}
                    {report.topServices?.length > 0 && (
                        <>
                            <h2 className={styles.h2Gap}><Scissors size={15} /> Top services</h2>
                            <ol className={styles.list}>
                                {report.topServices.slice(0, 5).map((s, i) => (
                                    <li key={s.name}><i>{i + 1}</i><span>{s.name}<small>{s.count}×</small></span><b>{inr(s.amount)}</b></li>
                                ))}
                            </ol>
                        </>
                    )}
                </section>
            )}
        </div>
    );
}
